import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Box,
  Button,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
} from '@mui/material';
import { Login as LoginIcon, Menu as MenuIcon } from '@mui/icons-material';
import EncycGamesLogo from '@components/layout/EncycGamesLogo';
import { brandColors } from '@config/brandTokens';
import { getContestSports, toSportSlug } from '@utils/sports';
import { isFeatureEnabled } from '@config/releaseProfile';
import { useActiveSession } from '@hooks/useActiveSession';
import { getPostLoginPath } from '@utils/authRouting';
import { mapPublicContestUrlToPortal } from '@utils/contestSubmitNavigation';

interface PublicSiteHeaderProps {
  activeSport?: string;
}

const ACTIVITY_NAV = [
  { label: 'Motor & Psychomotor', path: '/motor-psychomotor' },
  { label: 'Fitness Standards', path: '/fitness-standards' },
] as const;

const SITE_NAV = [
  { label: 'Blogs', path: '/blogs' },
  { label: 'Subscriptions', path: '/subscriptions' },
  { label: 'Sponsors & Organizers', path: '/partners' },
] as const;

const navBtnSx = (active: boolean, tone: 'activity' | 'site') => {
  const accent = tone === 'activity' ? brandColors.orange : brandColors.navy;
  const hoverIdle = tone === 'activity' ? 'rgba(232, 88, 11, 0.10)' : 'rgba(17, 23, 57, 0.06)';
  const hoverActive = tone === 'activity' ? '#c44609' : '#0a0f28';
  return {
    color: active ? brandColors.white : accent,
    bgcolor: active ? accent : 'transparent',
    textTransform: 'none' as const,
    minWidth: 'auto',
    px: { md: 0.75, lg: 1 },
    py: 0.55,
    fontSize: { md: '0.82rem', lg: '0.9rem' },
    fontWeight: active ? 700 : 600,
    fontFamily: '"Sora", Arial, sans-serif',
    letterSpacing: 0.01,
    lineHeight: 1.25,
    whiteSpace: 'nowrap' as const,
    boxShadow: 'none',
    '&:hover': {
      bgcolor: active ? hoverActive : hoverIdle,
      boxShadow: 'none',
    },
    '&.Mui-disabled': {
      color: accent,
      opacity: 1,
    },
  };
};

const PublicSiteHeader: React.FC<PublicSiteHeaderProps> = ({ activeSport }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const sports = getContestSports();
  const [mobileAnchor, setMobileAnchor] = useState<null | HTMLElement>(null);
  const { signedIn, user } = useActiveSession();
  const currentReturn = `${location.pathname}${location.search}`;
  const loginPath = `/login?returnUrl=${encodeURIComponent(currentReturn)}`;
  const signedInHome =
    signedIn && user
      ? location.pathname.startsWith('/contests/')
        ? mapPublicContestUrlToPortal(user, currentReturn)
        : getPostLoginPath(user)
      : '/login';

  const contestPreviewPath = (sportName: string) => `/contests/${toSportSlug(sportName)}`;

  const isSportActive = (sportName: string) => {
    if (activeSport) {
      return activeSport.toLowerCase() === sportName.toLowerCase();
    }
    const slug = toSportSlug(sportName);
    return (
      location.pathname === `/contests/${slug}` ||
      location.pathname === `/contests/${slug}/leaderboard` ||
      location.pathname.startsWith(`/contests/${slug}/`) ||
      location.pathname === `/sport/${slug}`
    );
  };

  const isPathActive = (path: string) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  const go = (path: string) => {
    setMobileAnchor(null);
    navigate(path);
  };

  return (
    <AppBar
      position="sticky"
      sx={{
        bgcolor: brandColors.white,
        boxShadow: '0 1px 4px rgba(17,23,57,0.08)',
        zIndex: (theme) => theme.zIndex.drawer + 1,
      }}
    >
      <Toolbar
        sx={{
          gap: { xs: 0.75, md: 0.5, lg: 0.75 },
          minHeight: { xs: 56, md: 64 },
          py: { md: 0.5 },
          flexWrap: 'nowrap',
          alignItems: 'center',
        }}
      >
        <Box
          onClick={() => navigate('/')}
          sx={{ cursor: 'pointer', display: 'inline-flex', flexShrink: 0 }}
          aria-label="EncycGames home"
        >
          <EncycGamesLogo width={168} sx={{ display: { xs: 'none', sm: 'inline-flex' } }} />
          <EncycGamesLogo width={150} sx={{ display: { xs: 'inline-flex', sm: 'none' } }} />
        </Box>

        <IconButton
          aria-label="Open menu"
          onClick={(e) => setMobileAnchor(e.currentTarget)}
          sx={{ display: { xs: 'inline-flex', md: 'none' }, color: brandColors.navy, ml: 'auto' }}
        >
          <MenuIcon />
        </IconButton>
        <Menu
          anchorEl={mobileAnchor}
          open={Boolean(mobileAnchor)}
          onClose={() => setMobileAnchor(null)}
          keepMounted
        >
          {sports.map((sportName) => (
            <MenuItem
              key={sportName}
              selected={isSportActive(sportName)}
              onClick={() => go(contestPreviewPath(sportName))}
              sx={{ fontFamily: '"Sora", Arial, sans-serif', fontWeight: 600, color: brandColors.orange }}
            >
              {sportName}
            </MenuItem>
          ))}
          {ACTIVITY_NAV.map((item) => (
            <MenuItem
              key={item.path}
              selected={isPathActive(item.path)}
              onClick={() => go(item.path)}
              sx={{ fontFamily: '"Sora", Arial, sans-serif', fontWeight: 600, color: brandColors.orange }}
            >
              {item.label}
            </MenuItem>
          ))}
          <Divider />
          {SITE_NAV.map((item) => (
            <MenuItem
              key={item.path}
              selected={isPathActive(item.path)}
              onClick={() => go(item.path)}
              sx={{ fontFamily: '"Sora", Arial, sans-serif', fontWeight: 600, color: brandColors.navy }}
            >
              {item.label}
            </MenuItem>
          ))}
          <Divider />
          <MenuItem
            onClick={() => go(signedIn ? signedInHome : loginPath)}
            sx={{ fontFamily: '"Sora", Arial, sans-serif', fontWeight: 700 }}
          >
            {signedIn ? 'My contests' : 'Sign in'}
          </MenuItem>
        </Menu>

        <Stack
          direction="row"
          spacing={0.15}
          alignItems="center"
          useFlexGap
          flexWrap="nowrap"
          sx={{
            display: { xs: 'none', md: 'flex' },
            flex: '1 1 auto',
            minWidth: 0,
            overflow: 'hidden',
          }}
        >
          {sports.map((sportName) => {
            const active = isSportActive(sportName);
            return (
              <Button
                key={sportName}
                variant={active ? 'contained' : 'text'}
                disableElevation
                onClick={() => navigate(contestPreviewPath(sportName))}
                sx={navBtnSx(active, 'activity')}
              >
                {sportName}
              </Button>
            );
          })}
          {ACTIVITY_NAV.map((item) => {
            const active = isPathActive(item.path);
            return (
              <Button
                key={item.path}
                variant={active ? 'contained' : 'text'}
                disableElevation
                onClick={() => navigate(item.path)}
                sx={navBtnSx(active, 'activity')}
              >
                {item.label}
              </Button>
            );
          })}
          <Box
            aria-hidden
            sx={{
              width: '1px',
              alignSelf: 'stretch',
              bgcolor: '#d8dee9',
              mx: 0.75,
              my: 0.4,
            }}
          />
          {SITE_NAV.map((item) => {
            const active = isPathActive(item.path);
            return (
              <Button
                key={item.path}
                variant={active ? 'contained' : 'text'}
                disableElevation
                onClick={() => navigate(item.path)}
                sx={navBtnSx(active, 'site')}
              >
                {item.label}
              </Button>
            );
          })}
        </Stack>

        {isFeatureEnabled('publicSponsors') && (
          <Button
            variant="text"
            onClick={() => navigate('/sponsors')}
            sx={{ display: { xs: 'none', lg: 'inline-flex' }, color: brandColors.violet, fontWeight: 600, textTransform: 'none' }}
          >
            Sponsors
          </Button>
        )}
        {isFeatureEnabled('publicTeam') && (
          <Button
            variant="text"
            onClick={() => navigate('/team')}
            sx={{ display: { xs: 'none', xl: 'inline-flex' }, color: brandColors.blue, textTransform: 'none' }}
          >
            Team
          </Button>
        )}
        {isFeatureEnabled('publicPricing') && (
          <Button
            variant="outlined"
            onClick={() => navigate('/pricing')}
            sx={{
              display: { xs: 'none', lg: 'inline-flex' },
              borderColor: brandColors.blue,
              color: brandColors.blue,
              textTransform: 'none',
            }}
          >
            Pricing
          </Button>
        )}
        <Button
          variant="contained"
          color="secondary"
          startIcon={signedIn ? undefined : <LoginIcon />}
          onClick={() => navigate(signedIn ? signedInHome : loginPath)}
          sx={{
            display: { xs: 'none', md: 'inline-flex' },
            textTransform: 'none',
            fontWeight: 600,
            flexShrink: 0,
            ml: { md: 'auto' },
          }}
        >
          {signedIn ? 'My contests' : 'Sign in'}
        </Button>
      </Toolbar>
    </AppBar>
  );
};

export default PublicSiteHeader;
